import { Component, ElementRef, OnDestroy, OnInit, inject, input } from '@angular/core';
import { EngineCultureService } from '@core/engine-culture/services/culture.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { closest } from '@progress/kendo-angular-common';
import {
  DateRangeComponent,
  DateRangePopupComponent,
  DateRangePopupTemplateDirective,
  DateRangeSelectionDirective,
  MultiViewCalendarComponent,
} from '@progress/kendo-angular-dateinputs';
import {
  ColumnComponent,
  DateFilterMenuComponent,
  FilterService,
  PopupCloseEvent,
  SinglePopupService,
} from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-datetime-filter',
  templateUrl: './datetime-filter.component.html',
  styleUrls: ['./datetime-filter.component.scss'],
  imports: [
    ButtonComponent,
    DateRangeComponent,
    DateRangePopupComponent,
    DateRangePopupTemplateDirective,
    MultiViewCalendarComponent,
    DateRangeSelectionDirective,
    DateFilterMenuComponent,
    TranslatePipe,
  ],
})
export class DateTimeFilterComponent implements OnInit, OnDestroy {
  private _element = inject(ElementRef);
  private _popupService = inject(SinglePopupService);
  private _cultureService = inject(EngineCultureService);

  private _popupSubscription: Subscription;

  readonly column = input<ColumnComponent>(undefined);
  readonly filter = input<CompositeFilterDescriptor>(undefined);
  readonly filterService = input<FilterService>(undefined);
  readonly useTimeZone = input<boolean>(undefined);

  display: string = null;
  range = {
    start: null,
    end: null,
  };

  isAdvancedModeEnabled = false;

  constructor() {
    this._popupSubscription = this._popupService.onClose.subscribe((e: PopupCloseEvent) => {
      if (document.activeElement) {
        const closestElement = closest(
          document.activeElement,
          (node: HTMLElement) =>
            node === this._element.nativeElement || String(node.className).indexOf('date-range-filter') >= 0,
        );
        if (
          closestElement ||
          String(e.originalEvent.srcElement.className).indexOf('datetime-filter-button') >= 0 ||
          String(e.originalEvent.srcElement.parentNode.className).indexOf('datetime-filter-button') >= 0
        ) {
          e.preventDefault();
        }
      }
    });
  }

  ngOnInit(): void {
    const filter = this.filter();
    if (!filter) return;

    const lteFilter: FilterDescriptor = filter.filters.find(
      (x: FilterDescriptor) => x.operator && x.operator == 'lte',
    ) as FilterDescriptor;
    const gteFilter: FilterDescriptor = filter.filters.find(
      (x: FilterDescriptor) => x.operator && x.operator == 'gte',
    ) as FilterDescriptor;

    if (lteFilter) this.range.end = lteFilter.value;
    if (gteFilter) this.range.start = gteFilter.value;

    this.updateDisplayValue(this.convertToLocalZone(this.range.start), this.convertToLocalZone(this.range.end));
  }

  ngOnDestroy(): void {
    this._popupSubscription.unsubscribe();
  }

  clearFilter() {
    this.display = null;
    this.range.start = null;
    this.range.end = null;

    this.filterService().filter({
      filters: [],
      logic: 'and',
    });
  }

  selectionChange(value: any): void {
    this.range = value;
  }

  onAccept(popup) {
    let start = this.convertToValidTimeZone(this.range.start);
    let end = this.convertToValidTimeZone(this.range.end);

    this.changeFilterValue(start, end);
    this.updateDisplayValue(moment(this.range.start), moment(this.range.end));

    popup.toggle();
  }

  onFilterModeChange(e: UIEvent) {
    this.isAdvancedModeEnabled = !this.isAdvancedModeEnabled;
    this.clearFilter();

    e.preventDefault();
  }

  private updateDisplayValue(startDate: moment.Moment, endDate: moment.Moment): void {
    if (startDate == null || !startDate.isValid()) {
      this.display = null;
      return;
    }

    this.display = `${startDate.format(this._cultureService.cultureSettings.dateShortPattern.toUpperCase())}`;

    if (endDate && endDate.isValid()) {
      let endDateFormat = endDate.format(this._cultureService.cultureSettings.dateShortPattern.toUpperCase());
      if (endDateFormat != this.display) this.display += ` - ${endDateFormat}`;
    }
  }

  private changeFilterValue(startDate: moment.Moment, endDate: moment.Moment): void {
    if (startDate && !startDate.isValid()) {
      this.clearFilter();
      return;
    }

    const filterValue = this.filter();
    if (filterValue) {
      const lteFilter = filterValue.filters.find(
        (x: FilterDescriptor) => x && x.field == this.column().field && x.operator == 'lte',
      );
      if (lteFilter) filterValue.filters.splice(filterValue.filters.indexOf(lteFilter), 1);

      const gteFilter = filterValue.filters.find(
        (x: FilterDescriptor) => x && x.field == this.column().field && x.operator == 'gte',
      );
      if (gteFilter) filterValue.filters.splice(filterValue.filters.indexOf(gteFilter), 1);
    }

    const filter =
      filterValue ||
      <CompositeFilterDescriptor>{
        logic: 'and',
        filters: [],
      };

    if (!endDate || !endDate.isValid()) endDate = startDate.clone();

    const column = this.column();
    filter.filters.push({
      field: column.field,
      operator: 'gte',
      value: startDate.toDate(),
    });

    filter.filters.push({
      field: column.field,
      operator: 'lte',
      value: endDate.add(1, 'd').subtract(1, 'ms').toDate(),
    });

    this.filterService().filter(filter);
  }

  private convertToValidTimeZone(value: Date): moment.Moment {
    // Dates which come from the data picker are in local time zone
    // So we have to convert them to valid timezones
    const valueUtc: moment.Moment = moment(
      moment(value)
        .add(-1 * value.getTimezoneOffset(), 'minutes')
        .utc()
        .format(moment.HTML5_FMT.DATETIME_LOCAL_MS),
    );

    if (!this.useTimeZone()) {
      return valueUtc;
    }

    return moment(
      moment(valueUtc).add(new Date().getTimezoneOffset(), 'minutes').format(moment.HTML5_FMT.DATETIME_LOCAL_MS),
    );
  }

  private convertToLocalZone(value: Date): moment.Moment {
    return value != null
      ? moment(value).add(this.useTimeZone() ? -new Date().getTimezoneOffset() : 0, 'minutes')
      : null;
  }
}
